import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { FarkleGame, FarkleStateDocument, FarkleRollEvent } from '../games/farkle/logic';
import { bankScore, endFarkleGame, rollDice, selectDice } from '../games/farkle/service';
import { endPartyClient } from '../services/partyLifecycle';
import {
  completeCrowdFarkleGame,
  FarklePlayerRecord,
  acknowledgeFarklePlayerAction,
  removeFarklePlayer,
  startCrowdFarkleGame,
  subscribeToFarklePlayers,
  requeueFinishedPlayers,
} from '../games/farkle/lobby';

const safeOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://qroke-a6fed.web.app';
const hotDiceColors = ['#fbbf24', '#f472b6', '#34d399', '#60a5fa', '#f87171'];

const FarkleCrowdHostPage: React.FC = () => {
  const { partyId } = useParams<{ partyId: string }>();
  const navigate = useNavigate();
  const [gameDoc, setGameDoc] = useState<FarkleStateDocument | null>(null);
  const [players, setPlayers] = useState<FarklePlayerRecord[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [status, setStatus] = useState('Tap Roll Dice to kick things off once players are seated.');
  const [error, setError] = useState<string | null>(null);
  const [keptLog, setKeptLog] = useState<{ dice: number[]; points: number }[]>([]);
  const [showFarkleOverlay, setShowFarkleOverlay] = useState(false);
  const [activeFarkleEvent, setActiveFarkleEvent] = useState<FarkleRollEvent | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [finalRoundToast, setFinalRoundToast] = useState<{ leader: string; score: number } | null>(null);
  const finalRoundToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finalRoundTriggeredRef = useRef(false);
  const [showFinaleOverlay, setShowFinaleOverlay] = useState(false);
  const [finalRankings, setFinalRankings] = useState<{ name: string; score: number }[]>([]);
  const finaleShownRef = useRef(false);
  const lastTurnRef = useRef<number | null>(null);
  const prevRollKeyRef = useRef<string>('');
  const partyEndedRef = useRef(false);
  const lastActionNonceRef = useRef<Record<string, number>>({});
  const [showHotDiceCelebration, setShowHotDiceCelebration] = useState(false);
  const [hotDiceParticles, setHotDiceParticles] = useState<Array<{ id: number; left: number; delay: number; color: string; scale: number }>>([]);
  const hotDiceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const hotDiceBurstRef = useRef(0);
  const [diceTrayAnimating, setDiceTrayAnimating] = useState(false);
  const diceAnimationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [manualOverrideEnabled, setManualOverrideEnabled] = useState(false);
  const farkleOverlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFarkleEventAtRef = useRef<number | null>(null);
  const [showQueuePanel, setShowQueuePanel] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);


  useEffect(() => {
    if (!partyId) return;
    const unsubscribe = subscribeToFarklePlayers(partyId, (records) => {
      setPlayers(records);
    });
    return () => unsubscribe();
  }, [partyId]);

  useEffect(() => {
    if (!partyId) return;
    const ref = doc(db, 'parties', partyId, 'farkle', 'state');
    const unsubscribe = onSnapshot(ref, (snapshot) => {
      if (snapshot.exists()) {
        setGameDoc(snapshot.data() as FarkleStateDocument);
      } else {
        setGameDoc(null);
      }
    });
    return () => unsubscribe();
  }, [partyId]);

  useEffect(() => {
    if (!gameDoc) {
      lastTurnRef.current = null;
      setKeptLog([]);
      return;
    }
    if (lastTurnRef.current !== gameDoc.state.turn) {
      lastTurnRef.current = gameDoc.state.turn;
      setKeptLog([]);
      setSelectedIndices([]);
    }
  }, [gameDoc?.state.turn]);


  const waitingPlayers = useMemo(() => players.filter((p) => p.status === 'waiting'), [players]);
  const activePlayers = useMemo(() => players.filter((p) => p.status === 'playing'), [players]);
  const cooldownPlayers = useMemo(() => players.filter((p) => p.status === 'cooldown'), [players]);

  const currentPlayerName = gameDoc ? gameDoc.state.players[gameDoc.state.turn] : null;
  const joinUrl = partyId ? `${safeOrigin}/party/${partyId}/game/farkle/join` : '';
  const gameActive = !!(gameDoc && gameDoc.phase !== 'complete');
  const remoteControlLocked = gameActive && players.some((player) => player.status === 'playing');
  const manualControlsDisabled = remoteControlLocked && !manualOverrideEnabled;
  const standings = useMemo(() => {
    if (!gameDoc) return [];
    return gameDoc.state.players
      .map((player) => ({ name: player, score: gameDoc.state.scores[player] ?? 0 }))
      .sort((a, b) => b.score - a.score);
  }, [gameDoc]);
  const leaderboardLeader = standings[0];
  const roundFinished = !!(gameDoc && (gameDoc.state.gameOver || gameDoc.phase === 'complete'));
  const finalRoundActive = !!(gameDoc && gameDoc.state.lastRound && !roundFinished);
  const remainingFinalTurns = gameDoc?.state.finalRoundPlayers?.length ?? 0;
  const scoreboardEntries = useMemo(() => {
    if (!gameDoc) return [];
    return standings.map((entry, index) => ({
      ...entry,
      rank: index + 1,
      farkles: gameDoc.state.stats.farkleCounts[entry.name] ?? 0,
      streak: gameDoc.state.stats.farkleStreaks[entry.name] ?? 0,
      isCurrent: currentPlayerName === entry.name && !roundFinished,
      isLeader: index === 0,
    }));
  }, [gameDoc, standings, currentPlayerName, roundFinished]);
  const currentFarkleCount = currentPlayerName && gameDoc ? gameDoc.state.stats.farkleCounts[currentPlayerName] ?? 0 : 0;
  const currentFarkleStreak = currentPlayerName && gameDoc ? gameDoc.state.stats.farkleStreaks[currentPlayerName] ?? 0 : 0;

  const endPartyOnServer = useCallback(async () => {
    if (!partyId || partyEndedRef.current) return;
    try {
      await endPartyClient(partyId);
      partyEndedRef.current = true;
    } catch (error) {
      console.error('Failed to auto-end party', error);
    }
  }, [partyId]);

  useEffect(() => {
    if (!gameDoc) {
      finalRoundTriggeredRef.current = false;
      setFinalRoundToast(null);
      if (finalRoundToastTimerRef.current) {
        clearTimeout(finalRoundToastTimerRef.current);
        finalRoundToastTimerRef.current = null;
      }
      return;
    }
    if (gameDoc.state.lastRound && !finalRoundTriggeredRef.current) {
      finalRoundTriggeredRef.current = true;
      const leaderName = gameDoc.state.winner ?? leaderboardLeader?.name ?? 'Leader';
      const leaderScore =
        (leaderName && gameDoc.state.scores[leaderName] !== undefined
          ? gameDoc.state.scores[leaderName]
          : leaderboardLeader?.score) ?? 0;
      setFinalRoundToast({ leader: leaderName, score: leaderScore });
      if (finalRoundToastTimerRef.current) {
        clearTimeout(finalRoundToastTimerRef.current);
      }
      finalRoundToastTimerRef.current = setTimeout(() => {
        setFinalRoundToast(null);
      }, 9000);
    } else if (!gameDoc.state.lastRound) {
      finalRoundTriggeredRef.current = false;
      setFinalRoundToast(null);
      if (finalRoundToastTimerRef.current) {
        clearTimeout(finalRoundToastTimerRef.current);
        finalRoundToastTimerRef.current = null;
      }
    }
  }, [gameDoc, leaderboardLeader]);

  useEffect(() => {
    return () => {
      if (finalRoundToastTimerRef.current) {
        clearTimeout(finalRoundToastTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      void endPartyOnServer();
    };
  }, [endPartyOnServer]);

  const playHotDiceSound = useCallback(() => {
    try {
      if (typeof window === 'undefined') return;
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioCtx();
      }
      const ctx = audioContextRef.current;
      if (!ctx) return;
      if (ctx.state === 'suspended') {
        void ctx.resume();
      }
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(420, now);
      osc.frequency.exponentialRampToValueAtTime(880, now + 0.35);
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.linearRampToValueAtTime(0.25, now + 0.08);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.8);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.8);
    } catch (error) {
      console.error('Hot dice audio failed', error);
    }
  }, []);

  const triggerHotDiceCelebration = useCallback(() => {
    hotDiceBurstRef.current += 1;
    setShowHotDiceCelebration(true);
    setHotDiceParticles(
      Array.from({ length: 22 }).map((_, idx) => ({
        id: hotDiceBurstRef.current * 100 + idx,
        left: Math.random() * 100,
        delay: Math.random() * 0.45,
        scale: 0.75 + Math.random() * 0.6,
        color: hotDiceColors[idx % hotDiceColors.length],
      }))
    );
    playHotDiceSound();
    if (hotDiceTimerRef.current) {
      clearTimeout(hotDiceTimerRef.current);
    }
    hotDiceTimerRef.current = setTimeout(() => {
      setShowHotDiceCelebration(false);
    }, 2200);
  }, [playHotDiceSound]);

  const triggerDiceTrayAnimation = useCallback(() => {
    setDiceTrayAnimating(true);
    if (diceAnimationTimerRef.current) {
      clearTimeout(diceAnimationTimerRef.current);
    }
    diceAnimationTimerRef.current = setTimeout(() => {
      setDiceTrayAnimating(false);
    }, 750);
  }, []);

  useEffect(() => {
    if (!gameDoc) return;
    const rollKey = `${gameDoc.state.rolledDice.join('-')}|${gameDoc.state.mustPick}`;
    if (rollKey && rollKey !== prevRollKeyRef.current) {
      prevRollKeyRef.current = rollKey;
      if (gameDoc.state.rolledDice.length) {
        triggerDiceTrayAnimation();
      }
      const scoringGame = new FarkleGame(gameDoc.state.players, gameDoc.settings);
      const score = scoringGame.scoreRoll(gameDoc.state.rolledDice);
    }
  }, [gameDoc?.state.rolledDice, gameDoc?.state.mustPick, triggerDiceTrayAnimation]);

  useEffect(() => {
    const event = gameDoc?.state.lastRollEvent ?? null;
    if (!event || event.outcome !== 'farkle') {
      return;
    }
    if (lastFarkleEventAtRef.current === event.timestamp) {
      return;
    }
    lastFarkleEventAtRef.current = event.timestamp;
    setActiveFarkleEvent(event);
    setShowFarkleOverlay(true);
    if (farkleOverlayTimerRef.current) {
      clearTimeout(farkleOverlayTimerRef.current);
    }
    farkleOverlayTimerRef.current = setTimeout(() => {
      setShowFarkleOverlay(false);
      setActiveFarkleEvent(null);
    }, 2400);
  }, [gameDoc?.state.lastRollEvent]);

  useEffect(() => {
    return () => {
      if (hotDiceTimerRef.current) {
        clearTimeout(hotDiceTimerRef.current);
      }
      if (audioContextRef.current) {
        void audioContextRef.current.close().catch(() => undefined);
      }
      if (diceAnimationTimerRef.current) {
        clearTimeout(diceAnimationTimerRef.current);
      }
      if (farkleOverlayTimerRef.current) {
        clearTimeout(farkleOverlayTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!partyId || !gameDoc) return;
    players.forEach((player) => {
      const action = player.controlAction;
      if (!action) return;
      if (lastActionNonceRef.current[player.id] === action.nonce) {
        return;
      }
      lastActionNonceRef.current[player.id] = action.nonce;
      const resolvedPlayerName = (() => {
        if (typeof player.seatIndex === 'number') {
          return gameDoc.state.players[player.seatIndex] ?? player.name;
        }
        return player.name;
      })();
      const isTurn = gameDoc.state.players[gameDoc.state.turn] === resolvedPlayerName;
      const process = async () => {
        try {
          if (!isTurn) {
            throw new Error('Not your turn.');
          }
          switch (action.type) {
            case 'roll':
              await rollDice(partyId, resolvedPlayerName);
              if (typeof action.power === 'number') {
                const pct = Math.round(Math.min(1, Math.max(0, action.power)) * 100);
                setStatus(`${resolvedPlayerName} let it fly at ${pct}% power.`);
              } else {
                setStatus(`${resolvedPlayerName} rolled from their phone.`);
              }
              triggerDiceTrayAnimation();
              setSelectedIndices([]);
              break;
            case 'select':
              if (!action.indices?.length) {
                throw new Error('Select at least one scoring die.');
              }
              {
                const diceValues = action.indices
                  .map((idx) => gameDoc.state.rolledDice[idx])
                  .filter((value): value is number => typeof value === 'number');
                const scoringGame = new FarkleGame(gameDoc.state.players, gameDoc.settings);
                const points = diceValues.length ? scoringGame.scoreRoll(diceValues) : 0;
                const result = await selectDice(partyId, resolvedPlayerName, action.indices);
                setStatus(result.hotDice ? `${resolvedPlayerName} triggered hot dice!` : `${resolvedPlayerName} locked dice.`);
                if (points > 0) {
                  setKeptLog((prev) => [...prev, { dice: diceValues, points }]);
                }
                if (result.hotDice) {
                  triggerHotDiceCelebration();
                }
                setSelectedIndices([]);
              }
              break;
            case 'bank':
              await bankScore(partyId, resolvedPlayerName);
              setStatus(`${resolvedPlayerName} banked their score.`);
              setKeptLog([]);
              setSelectedIndices([]);
              break;
            default:
              throw new Error('Unknown action.');
          }
          setError(null);
        } catch (err) {
          console.error('Remote player action failed', err);
          setError((err as Error).message ?? 'Unable to process player action.');
        } finally {
          await acknowledgeFarklePlayerAction(partyId, player.id, action.nonce).catch(() => undefined);
        }
      };
      void process();
    });
  }, [partyId, gameDoc, players, triggerHotDiceCelebration, triggerDiceTrayAnimation]);

  useEffect(() => {
    if (!gameDoc) {
      finaleShownRef.current = false;
      setShowFinaleOverlay(false);
      setFinalRankings([]);
      return;
    }
    const finished = roundFinished;
    if (finished && !finaleShownRef.current) {
      finaleShownRef.current = true;
      setFinalRankings(standings.map((entry) => ({ ...entry })));
      setShowFinaleOverlay(true);
    } else if (!finished) {
      finaleShownRef.current = false;
      setShowFinaleOverlay(false);
    }
  }, [gameDoc, roundFinished, standings]);

  const toggleDiceIndex = (idx: number) => {
    if (!gameDoc?.state.rolledDice.length || manualControlsDisabled) return;
    setSelectedIndices((prev) => {
      if (prev.includes(idx)) {
        return prev.filter((value) => value !== idx);
      }
      return [...prev, idx].sort((a, b) => a - b);
    });
  };

  const previewSelectionScore = useCallback(
    (indices: number[]): number => {
      if (!gameDoc) return 0;
      const dice = indices.map((i) => gameDoc.state.rolledDice[i]).filter((value) => typeof value === 'number');
      if (!dice.length) return 0;
      const scoringGame = new FarkleGame(gameDoc.state.players, gameDoc.settings);
      return scoringGame.scoreRoll(dice);
    },
    [gameDoc]
  );

  useEffect(() => {
    if (!selectedIndices.length) return;
    const points = previewSelectionScore(selectedIndices);
    if (points > 0) {
      const projected = (gameDoc?.state.tentativeScore ?? 0) + points;
      setStatus(`Selected dice worth ${points.toLocaleString()} pts — tentative ${projected.toLocaleString()} pts.`);
      setError(null);
    } else if (selectedIndices.length) {
      setStatus('Those dice are not a scoring combo yet.');
    }
  }, [selectedIndices, previewSelectionScore, gameDoc?.state.tentativeScore]);

  const handleStartGame = async () => {
    if (!partyId || actionBusy) return;
    try {
      setActionBusy(true);
      const result = await startCrowdFarkleGame(partyId);
      setGameDoc(result.doc);
      setStatus('Game launched — roll when everyone is ready.');
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  const handleRoll = async () => {
    if (!partyId || !gameDoc || !currentPlayerName || actionBusy) return;
    if (manualControlsDisabled) {
      setStatus('Phones are driving this turn. Enable manual override to roll from the host screen.');
      return;
    }
    try {
      setActionBusy(true);
      setError(null);
      await rollDice(partyId, currentPlayerName);
      triggerDiceTrayAnimation();
      setSelectedIndices([]);
      setStatus(`Rolled ${gameDoc.state.rolledDice.join(', ')} — pick scoring combos.`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  const handleKeep = async () => {
    if (!partyId || !gameDoc || !currentPlayerName || actionBusy) return;
    if (manualControlsDisabled) {
      setStatus('Phones are driving this turn. Enable manual override to keep dice manually.');
      return;
    }
    if (!selectedIndices.length) {
      setStatus('Select at least one scoring die to lock.');
      return;
    }
    const points = previewSelectionScore(selectedIndices);
    if (points === 0) {
      setStatus('Those dice are not scoring yet.');
      return;
    }
    try {
      setActionBusy(true);
      const result = await selectDice(partyId, currentPlayerName, selectedIndices);
      setKeptLog((prev) => [...prev, { dice: selectedIndices.map((i) => gameDoc.state.rolledDice[i]), points }]);
      setStatus(result.hotDice ? '🔥 Hot dice! Everything resets — roll again.' : 'Dice locked. Roll again or bank.');
      if (result.hotDice) {
        triggerHotDiceCelebration();
      }
      setError(null);
      setSelectedIndices([]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  const handleBank = async () => {
    if (!partyId || !gameDoc || !currentPlayerName || actionBusy) return;
    if (manualControlsDisabled) {
      setStatus('Phones are driving this turn. Enable manual override to bank from the host screen.');
      return;
    }
    try {
      setActionBusy(true);
      await bankScore(partyId, currentPlayerName);
      setSelectedIndices([]);
      setKeptLog([]);
      setStatus('Score banked — next player up.');
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  const handleEndGame = async (requeue: boolean) => {
    if (!partyId || !gameDoc || actionBusy) return;
    try {
      setActionBusy(true);
      await endFarkleGame(partyId);
      await completeCrowdFarkleGame(partyId, gameDoc.gameId, { requeuePlayers: requeue });
      setStatus(requeue ? 'Game ended. Everyone is back in line.' : 'Game ended. Players sent to cooldown.');
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  const handleExitToDashboard = async () => {
    try {
      setActionBusy(true);
      if (partyId && gameDoc && gameActive) {
        try {
          await endFarkleGame(partyId);
          await completeCrowdFarkleGame(partyId, gameDoc.gameId, { requeuePlayers: false });
        } catch (error) {
          console.error('Failed to close Farkle game before exit', error);
        }
      }
      await endPartyOnServer();
    } finally {
      setActionBusy(false);
      navigate('/dashboard');
    }
  };

  const handleRequeueCooldown = async () => {
    if (!partyId || !cooldownPlayers.length) return;
    try {
      await requeueFinishedPlayers(partyId, cooldownPlayers.map((p) => p.id));
      setStatus('Cooldown players moved back into the waiting line.');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const renderDiceTray = () => {
    const dice = gameDoc?.state.rolledDice ?? [];
    if (!dice.length) {
      return (
        <div className="mt-3 rounded-2xl border border-dashed border-emerald-400/40 px-4 py-10 text-center text-emerald-200/70">
          Roll to populate the tray.
        </div>
      );
    }
    return (
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
        {dice.map((value, idx) => {
          const active = selectedIndices.includes(idx);
          const animationStyle = diceTrayAnimating ? ({ animationDelay: `${idx * 0.05}s` } as React.CSSProperties) : undefined;
          return (
            <button
              key={`${value}-${idx}`}
              onClick={() => toggleDiceIndex(idx)}
              className={`rounded-3xl border px-5 py-4 text-center transition ${
                active
                  ? 'border-lime-300/90 bg-emerald-500/20 text-white shadow-lg shadow-emerald-900'
                  : 'border-emerald-500/30 bg-slate-900/70 text-emerald-100/80 hover:border-emerald-300/70'
              } ${diceTrayAnimating ? 'dice-roll-animate' : ''}`}
              style={animationStyle}
            >
              <div className="text-4xl">🎲</div>
              <div className="text-3xl font-semibold">{value}</div>
              <p className="text-xs text-emerald-200/70">{active ? 'Selected' : 'Tap to keep'}</p>
            </button>
          );
        })}
      </div>
    );
  };

  const renderKeptLog = () => (
    <div className="rounded-2xl border border-emerald-500/20 bg-slate-900/60 p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Kept Dice Log</p>
        {keptLog.length > 0 && (
          <span className="text-[0.65rem] uppercase tracking-[0.2em] text-emerald-200/70">{keptLog.length} keeps</span>
        )}
      </div>
      {keptLog.length === 0 ? (
        <p className="mt-3 text-sm text-emerald-100/70">Log resets each turn — perfect for flexing hot dice threads.</p>
      ) : (
        <div className="mt-3 max-h-48 space-y-2 overflow-y-auto pr-1">
          {keptLog.map((entry, idx) => (
            <div
              key={`kept-entry-${idx}`}
              className="flex items-center justify-between rounded-xl border border-emerald-500/20 bg-emerald-900/30 px-3 py-2"
            >
              <div className="flex flex-wrap gap-1 text-sm text-emerald-100">
                {entry.dice.map((value, dieIdx) => (
                  <span
                    key={`kept-die-${idx}-${dieIdx}`}
                    className="rounded-lg bg-emerald-800/70 px-2 py-1 text-base font-semibold text-emerald-50"
                  >
                    {value}
                  </span>
                ))}
              </div>
              <span className="text-sm font-bold text-lime-300">+{entry.points.toLocaleString()} pts</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderScoreboard = () => {
    if (!scoreboardEntries.length) return null;
    const leaderName = leaderboardLeader?.name ?? null;
    const leaderScore = leaderboardLeader?.score ?? 0;
    return (
      <div className="rounded-3xl border border-emerald-600/30 bg-emerald-950/40 p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Leaderboard</p>
        <div className="mt-4 space-y-2">
          {scoreboardEntries.map((entry) => {
            const statusLabel = roundFinished
              ? entry.isLeader
                ? 'Champion'
                : 'Final standing'
              : entry.isCurrent
              ? 'Taking a turn'
              : finalRoundActive && entry.isLeader
              ? 'Score to beat'
              : 'Waiting';
            return (
              <div
                key={entry.name}
                className={`rounded-2xl border px-4 py-3 transition ${
                  entry.isLeader && roundFinished
                    ? 'border-amber-400/70 bg-amber-900/20 text-amber-100'
                    : entry.isCurrent
                    ? 'border-emerald-400/50 bg-emerald-900/60 text-emerald-100'
                    : 'border-transparent bg-emerald-900/20 text-emerald-100/90'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-semibold">
                      #{entry.rank} {entry.name}
                    </p>
                    <p className="text-xs text-emerald-200/80">{statusLabel}</p>
                  </div>
                  <p className="text-2xl font-bold text-emerald-100">{entry.score.toLocaleString()}</p>
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-emerald-200/70">
                  <span className="rounded-full border border-emerald-400/30 px-2 py-1">💀 {entry.farkles}</span>
                  <span className="rounded-full border border-emerald-400/30 px-2 py-1">Streak {entry.streak}</span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 rounded-2xl border border-emerald-400/30 bg-slate-950/70 px-4 py-3 text-sm text-emerald-100/80">
          {roundFinished
            ? '🎉 Round over — announce the champ or spin up another game.'
            : finalRoundActive && leaderName
            ? `${leaderName} set ${leaderScore.toLocaleString()} pts. ${remainingFinalTurns} player${remainingFinalTurns === 1 ? '' : 's'} can still steal the crown.`
            : gameDoc?.state.hotDice
            ? '🔥 Hot dice unlocked — reroll all six.'
            : gameDoc?.state.mustPick
            ? 'Select at least one scoring set before rolling again.'
            : 'Roll or bank to chase 10K.'}
        </div>
      </div>
    );
  };

  const renderQueueList = (list: FarklePlayerRecord[], title: string, empty: string, actions?: (player: FarklePlayerRecord) => React.ReactNode) => (
    <div className="rounded-2xl border border-emerald-500/30 bg-slate-950/60 p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">{title}</p>
        <span className="text-[0.65rem] uppercase tracking-[0.2em] text-emerald-200/70">{list.length}</span>
      </div>
      {list.length === 0 ? (
        <p className="mt-3 text-sm text-emerald-100/70">{empty}</p>
      ) : (
        <div className="mt-3 space-y-2">
          {list.map((player, index) => (
            <div key={player.id} className="flex items-center justify-between rounded-xl border border-emerald-500/20 bg-emerald-900/30 px-3 py-2">
              <div>
                <p className="text-sm font-semibold text-emerald-100">{player.name}</p>
                <p className="text-xs text-emerald-200/70">#{index + 1}</p>
              </div>
              {actions && <div className="flex items-center gap-2">{actions(player)}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (!partyId) {
    return <div className="p-6 text-white">Missing party id.</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-950 via-slate-950 to-slate-900 text-white">
      {finalRoundToast && (
        <div className="fixed top-6 right-6 z-30 w-80 rounded-2xl border border-amber-400/60 bg-amber-950/70 px-5 py-4 shadow-2xl shadow-amber-900/40">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-amber-300">Final Round</p>
              <p className="mt-1 text-sm font-semibold text-amber-100">
                {finalRoundToast.leader} set the bar at {finalRoundToast.score.toLocaleString()} pts.
              </p>
              <p className="text-xs text-amber-200/80">Everyone else gets one more shot to steal it.</p>
            </div>
            <button
              onClick={() => setFinalRoundToast(null)}
              className="rounded-full border border-amber-400/60 px-2 py-1 text-xs font-semibold text-amber-100 hover:bg-amber-400/20"
            >
              X
            </button>
          </div>
        </div>
      )}
      {showHotDiceCelebration && (
        <div className="fixed inset-0 z-45 flex items-center justify-center overflow-hidden">
          <div className="pointer-events-none absolute inset-0">
            {hotDiceParticles.map((particle) => {
              const style: React.CSSProperties = {
                left: `${particle.left}%`,
                animationDelay: `${particle.delay}s`,
                animationDuration: `${1.4 + particle.delay}s`,
                backgroundColor: particle.color,
                width: `${0.4 + particle.scale * 0.4}rem`,
                height: `${0.18 + particle.scale * 0.12}rem`,
              };
              return <span key={particle.id} className="hot-dice-confetti" style={style} />;
            })}
          </div>
          <div className="pointer-events-none relative rounded-[32px] border border-amber-400/80 bg-gradient-to-br from-amber-900/90 via-orange-900/70 to-rose-900/70 px-10 py-8 text-center shadow-[0_0_60px_rgba(251,191,36,0.45)]">
            <p className="text-xs uppercase tracking-[0.6em] text-amber-200">Hot Dice</p>
            <p className="mt-2 text-5xl font-black text-amber-50 drop-shadow-[0_6px_20px_rgba(251,191,36,0.45)]">Keep it rolling!</p>
            <p className="mt-3 text-base text-amber-100/90">All six dice reset — crowd goes wild!</p>
          </div>
        </div>
      )}
      {showFinaleOverlay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-lg">
          <div className="relative w-full max-w-3xl rounded-[32px] border border-amber-400/40 bg-gradient-to-br from-slate-950 via-amber-950/40 to-emerald-950/40 p-8 text-center shadow-[0_0_60px_rgba(250,204,21,0.25)]">
            <button
              onClick={() => setShowFinaleOverlay(false)}
              className="absolute right-4 top-4 rounded-full border border-amber-400/60 px-3 py-1 text-xs font-semibold text-amber-100 hover:bg-amber-400/20"
            >
              Close
            </button>
            <p className="text-xs uppercase tracking-[0.5em] text-amber-300">Victory Lap</p>
            <h2 className="mt-4 text-4xl font-black text-amber-100">
              {(finalRankings[0]?.name ?? 'Champion')} takes the lily pad!
            </h2>
            <p className="mt-2 text-sm text-amber-200/80">
              {(finalRankings[0]?.score ?? 0).toLocaleString()} pts • {finalRankings.length} player{finalRankings.length === 1 ? '' : 's'}
            </p>
            <div className="mt-6 space-y-3 text-left">
              {finalRankings.map((entry, idx) => (
                <div
                  key={`${entry.name}-${idx}`}
                  className={`flex items-center justify-between rounded-2xl border px-4 py-3 ${
                    idx === 0
                      ? 'border-amber-400/70 bg-amber-900/25 text-amber-100'
                      : 'border-emerald-400/30 bg-slate-900/70 text-emerald-100'
                  }`}
                >
                  <div className="flex items-center gap-3 text-lg font-semibold">
                    <span className={idx === 0 ? 'text-amber-300' : 'text-emerald-300'}>#{idx + 1}</span>
                    <span>{entry.name}</span>
                  </div>
                  <span className="text-2xl font-bold">{entry.score.toLocaleString()} pts</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {showFarkleOverlay && activeFarkleEvent && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.5em] text-rose-200">{activeFarkleEvent.player} farkled</p>
            <p className="mt-2 text-6xl font-black uppercase tracking-[0.6em] text-rose-200 drop-shadow-[0_0_35px_rgba(248,113,113,0.75)] animate-pulse">
              FARKLE
            </p>
            <p className="mt-4 text-lg font-semibold text-rose-100">Sad trombone... no scoring dice.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {activeFarkleEvent.dice.map((die, idx) => (
                <div key={`farkle-die-${idx}`} className="flex h-16 w-16 items-center justify-center rounded-2xl border border-rose-400/60 bg-rose-900/40 text-3xl font-bold text-rose-100">
                  {die}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-8">
        <header className="flex flex-col gap-3 rounded-3xl border border-emerald-500/30 bg-emerald-950/60 p-6 shadow-2xl shadow-emerald-900/40 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-emerald-300">Frog Lab</p>
            <h1 className="text-3xl font-semibold">Farkle Crowd Mode</h1>
            <p className="text-sm text-emerald-100/80">Party {partyId}</p>
          </div>
          <div className="flex flex-wrap gap-3 text-sm text-emerald-100/80">
            <span className="rounded-full bg-emerald-900/70 px-4 py-2">🐸 QR-ready mini game</span>
            <button
              onClick={handleExitToDashboard}
              disabled={actionBusy}
              className="rounded-full border border-emerald-400/60 px-4 py-2 text-emerald-200 hover:bg-emerald-500/10 disabled:opacity-60"
            >
              ← Exit & End Party
            </button>
          </div>
        </header>

        <section className="grid gap-6 lg:grid-cols-[320px,1fr]">
          <div className="flex flex-col gap-4">
            {renderScoreboard()}
            <div className="rounded-3xl border border-emerald-600/30 bg-slate-950/60 p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Line Status</p>
                <span className="text-[0.65rem] uppercase tracking-[0.3em] text-emerald-200/80">
                  {waitingPlayers.length + activePlayers.length + cooldownPlayers.length} players
                </span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-center text-sm text-emerald-100/80">
                <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/30 px-3 py-2">
                  <p className="text-xs uppercase tracking-[0.2em] text-emerald-200/70">Waiting</p>
                  <p className="text-2xl font-bold text-emerald-100">{waitingPlayers.length}</p>
                </div>
                <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/30 px-3 py-2">
                  <p className="text-xs uppercase tracking-[0.2em] text-emerald-200/70">Playing</p>
                  <p className="text-2xl font-bold text-emerald-100">{activePlayers.length}</p>
                </div>
                <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/30 px-3 py-2">
                  <p className="text-xs uppercase tracking-[0.2em] text-emerald-200/70">Cooldown</p>
                  <p className="text-2xl font-bold text-emerald-100">{cooldownPlayers.length}</p>
                </div>
              </div>
              <button
                onClick={() => setShowQueuePanel(true)}
                className="mt-4 w-full rounded-2xl border border-emerald-400/70 px-4 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-400/10"
              >
                Manage Line & Seating
              </button>
            </div>
            <div className="rounded-3xl border border-emerald-600/30 bg-slate-950/60 p-5">
              <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Crowd Controls</p>
              <p className="mt-2 text-sm text-emerald-100/80">
                Phones are the default remote. Flip override only if a controller gets stuck mid-turn.
              </p>
              {remoteControlLocked ? (
                <button
                  onClick={() => setManualOverrideEnabled((prev) => !prev)}
                  className="mt-4 w-full rounded-2xl border border-emerald-400/60 px-4 py-2 text-xs font-semibold text-emerald-100 hover:bg-emerald-400/10"
                >
                  {manualOverrideEnabled ? 'Disable Manual Override' : 'Enable Manual Override'}
                </button>
              ) : (
                <p className="mt-3 text-xs text-emerald-200/70">Override unlocks automatically once a round is underway.</p>
              )}
            </div>
          </div>

          <div className="rounded-3xl border border-emerald-600/20 bg-slate-950/70 p-6 space-y-5">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Current Player</p>
                <p className="text-3xl font-semibold text-white">{currentPlayerName ?? 'Waiting for game'}</p>
                <p className="text-sm text-emerald-100/80">
                  Tentative {(gameDoc?.state.tentativeScore ?? 0).toLocaleString()} pts • Dice {gameDoc?.state.currentDice ?? 0}
                </p>
                {currentPlayerName && (
                  <p className="text-xs text-emerald-200/70">💀 Farkles {currentFarkleCount} • Streak {currentFarkleStreak}</p>
                )}
              </div>
              {!gameActive && (
                <button
                  onClick={handleStartGame}
                  disabled={actionBusy || waitingPlayers.length === 0}
                  className={`rounded-2xl px-4 py-2 text-sm font-semibold ${
                    waitingPlayers.length === 0 ? 'bg-gray-700 text-gray-400' : 'bg-emerald-500 text-slate-900 hover:bg-emerald-400'
                  }`}
                >
                  🚀 Start Game ({waitingPlayers.length} queued)
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleRoll}
                disabled={actionBusy || !gameActive || manualControlsDisabled}
                className="flex-1 min-w-[140px] rounded-2xl bg-emerald-500 px-4 py-3 text-center text-sm font-semibold text-slate-900 hover:bg-emerald-400 disabled:bg-gray-700 disabled:text-gray-400"
              >
                🎲 Roll Dice
              </button>
              <button
                onClick={handleKeep}
                disabled={actionBusy || !gameActive || manualControlsDisabled}
                className="flex-1 min-w-[140px] rounded-2xl border border-emerald-400 px-4 py-3 text-center text-sm font-semibold text-emerald-200 hover:bg-emerald-400/10 disabled:border-gray-600 disabled:text-gray-500"
              >
                ♻ Keep Selected
              </button>
              <button
                onClick={handleBank}
                disabled={actionBusy || !gameActive || manualControlsDisabled}
                className="flex-1 min-w-[140px] rounded-2xl border border-emerald-400/70 px-4 py-3 text-center text-sm font-semibold text-emerald-100 hover:bg-emerald-400/10 disabled:border-gray-600 disabled:text-gray-500"
              >
                💼 Bank Score
              </button>
            </div>

            {gameActive && (
              <div className="rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-950/70 to-slate-900/60 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Dice Tray</p>
                  <span className="text-[0.65rem] uppercase tracking-[0.3em] text-emerald-200/80">{gameDoc?.state.currentDice ?? 0} dice</span>
                </div>
                {renderDiceTray()}
              </div>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-slate-900/60 to-emerald-900/40 p-4">
                <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Status</p>
                <p className="mt-2 text-lg font-semibold text-emerald-100">{status}</p>
                {error && <p className="mt-2 rounded-lg bg-rose-900/40 px-3 py-2 text-sm text-rose-200">{error}</p>}
                {gameDoc && (
                  <p className="mt-2 text-xs text-emerald-200/70">
                    Current player: <span className="font-semibold text-emerald-100">{currentPlayerName ?? 'N/A'}</span> • Tentative {gameDoc.state.tentativeScore.toLocaleString()} pts
                  </p>
                )}
              </div>
              {gameActive && renderKeptLog()}
            </div>

            {gameActive && (
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => handleEndGame(true)}
                  disabled={actionBusy}
                  className="rounded-2xl border border-emerald-400/70 px-4 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-400/10"
                >
                  ✅ End Game & Requeue
                </button>
                <button
                  onClick={() => handleEndGame(false)}
                  disabled={actionBusy}
                  className="rounded-2xl border border-rose-400/70 px-4 py-2 text-sm font-semibold text-rose-100 hover:bg-rose-400/10"
                >
                  🛑 End Game (Cooldown)
                </button>
              </div>
            )}
            {roundFinished && !gameActive && (
              <div className="rounded-3xl border border-emerald-500/40 bg-emerald-900/30 p-5 space-y-3">
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-200">🎉 Game Over!</p>
                  <p className="text-sm text-emerald-100/80 mt-1">Keep the party rolling?</p>
                </div>
                <button
                  onClick={handleStartGame}
                  disabled={actionBusy || waitingPlayers.length === 0}
                  className={`w-full rounded-2xl px-4 py-3 text-base font-bold shadow-lg ${
                    waitingPlayers.length === 0
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-900 hover:from-emerald-400 hover:to-teal-400'
                  }`}
                >
                  🎮 Play Again ({waitingPlayers.length} {waitingPlayers.length === 1 ? 'player' : 'players'})
                </button>
                {waitingPlayers.length === 0 && cooldownPlayers.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-emerald-200/70 text-center">
                      No players in queue. Requeue from cooldown?
                    </p>
                    <button
                      onClick={handleRequeueCooldown}
                      className="w-full rounded-2xl border border-emerald-400/70 px-4 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-400/10"
                    >
                      Move {cooldownPlayers.length} from Cooldown to Queue
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
        {showQueuePanel && (
          <div className="fixed inset-0 z-40 flex">
            <button
              aria-label="Close queue panel"
              className="flex-1 bg-black/60"
              onClick={() => setShowQueuePanel(false)}
            />
            <aside className="h-full w-full max-w-md bg-slate-950/95 p-6 text-white shadow-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Manage Line</p>
                  <p className="text-lg font-semibold">Queues & Seating</p>
                </div>
                <button
                  onClick={() => setShowQueuePanel(false)}
                  className="rounded-full border border-emerald-400/60 px-3 py-1 text-xs text-emerald-100 hover:bg-emerald-400/10"
                >
                  Close
                </button>
              </div>
              <div className="mt-5 space-y-4 overflow-y-auto pr-2">
                {renderQueueList(
                  waitingPlayers,
                  'Waiting Queue',
                  'Nobody waiting yet. Drop the QR on screen to recruit players.',
                  (player) => [
                    <button
                      key="kick"
                      onClick={() => {
                        if (!partyId) return;
                        void removeFarklePlayer(partyId, player.id).catch((err) => setError((err as Error).message));
                      }}
                      className="rounded-full border border-emerald-400/60 px-3 py-1 text-xs text-emerald-100 hover:bg-emerald-400/10"
                    >
                      Remove
                    </button>,
                  ]
                )}
                {renderQueueList(activePlayers, 'Players In Game', 'Start a round to populate seats.')}
                {renderQueueList(
                  cooldownPlayers,
                  'Cooldown Crew',
                  'When a game finishes without requeue, seated players cool off here.',
                  (player) => [
                    <button
                      key="requeue"
                      onClick={() => {
                        if (!partyId) return;
                        void requeueFinishedPlayers(partyId, [player.id]).catch((err) => setError((err as Error).message));
                      }}
                      className="rounded-full border border-emerald-400/60 px-3 py-1 text-xs text-emerald-100 hover:bg-emerald-400/10"
                    >
                      Requeue
                    </button>,
                  ]
                )}
                {cooldownPlayers.length > 1 && (
                  <button
                    onClick={handleRequeueCooldown}
                    className="w-full rounded-2xl border border-emerald-400/70 px-4 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-400/10"
                  >
                    Requeue All Cooldown Players
                  </button>
                )}
              </div>
            </aside>
          </div>
        )}
      </div>
      <div className="fixed bottom-6 right-6 hidden flex-col items-center gap-2 rounded-3xl border border-emerald-500/30 bg-slate-950/90 p-4 shadow-2xl shadow-emerald-900/50 lg:flex">
        <div className="rounded-2xl bg-white p-3">
          <QRCode value={joinUrl} size={96} />
        </div>
        <p className="text-xs text-emerald-100/80">Scan to join</p>
      </div>
      <button
        onClick={() => setShowQrModal(true)}
        className="fixed bottom-6 right-6 rounded-full border border-emerald-400/60 bg-emerald-950/80 px-4 py-2 text-sm text-emerald-100 shadow-lg shadow-emerald-900/40 lg:hidden"
      >
        📱 QR
      </button>
      {showQrModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6">
          <div className="w-full max-w-sm rounded-3xl border border-emerald-500/40 bg-slate-950/90 p-6 text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Join This Game</p>
            <div className="mt-4 rounded-3xl bg-white p-4">
              <QRCode value={joinUrl} size={160} />
            </div>
            <p className="mt-3 text-sm text-emerald-100/80">{joinUrl}</p>
            <button
              onClick={() => setShowQrModal(false)}
              className="mt-4 w-full rounded-2xl border border-emerald-400/60 px-4 py-2 text-sm text-emerald-100 hover:bg-emerald-400/10"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarkleCrowdHostPage;
